import React from 'react';
import { MessageType, Message as MessageModel, Chat, TextMessage, VideoConferenceMessage, TemplateMessage, MediaMessage } from "../../../../chat-api";
import moment from "moment-with-locales-es6";
import { isMedia, hasCaption, hasTextOrCaption } from "../Message";
import { 
  useStyles, MessageInfo, MessageInfoContainer, MessageInfoContainerWithoutText, 
  MessageInfoWithoutText, MessageHour, VideoConferenceWrapper, MessageTextWrapper, MessageTextContent, MessageTextSpacer 
} from './styles';
import MissedVideoCallIcon from '@material-ui/icons/MissedVideoCall';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import MovieIcon from '@material-ui/icons/Movie';
import VideocamIcon from '@material-ui/icons/Videocam';
import { useAppState, useTheme } from '../../../../data';
import { ThemeContextTypes } from '../../../../data/ThemeContext';
import parse from '../../../../utils/whatsapp-parser';

interface MessageFooterProps {
  message: MessageModel;
  chat: Chat | null;
  textColor?: string;
  hourTextColor?: string;
}

const VideoConferenceText = React.memo(({ message, theme }: {message: VideoConferenceMessage, theme: ThemeContextTypes}) => {
  const { getTranslation } = useAppState();
  
  if (message.body.status === 'refused') {
    return (
      <VideoConferenceWrapper as="sdiv" theme={theme}>
        <MissedVideoCallIcon style={{color: 'red'}} /> &nbsp;
        {getTranslation('MESSAGE_VIDEO_REFUSED', [moment(message.date).format('HH:mm')])}
      </VideoConferenceWrapper>
    );
  } else {
    if (message.body.videoConference?.status === 'completed') {
      return (
        <VideoConferenceWrapper as="sdiv" theme={theme}>
          <MovieIcon style={{color: theme.body?.customer?.sliderTrackColor}} /> &nbsp;
          {getTranslation('MESSAGE_VIDEO_COMPLETED', [moment(message.body.videoConference?.endDate).format('HH:mm')])}
        </VideoConferenceWrapper>
      );
    } else {
      if (!!message.body?.link) {
        return (
          <VideoConferenceWrapper as="sdiv" theme={theme}>
            <VideoCallIcon /> &nbsp;
            {getTranslation('MESSAGE_VIDEO_ACCEPTED')}&nbsp;<a href={message?.body?.link} target='_blank' rel="noopener noreferrer">{getTranslation('MESSAGE_VIDEO_ACCEPTED_LINK')}</a> 
          </VideoConferenceWrapper>
        );
      } else {
        return (
          <VideoConferenceWrapper as="sdiv" theme={theme}>
            <VideocamIcon /> &nbsp;
            {getTranslation('MESSAGE_VIDEO_WAITING')}
          </VideoConferenceWrapper>
        );
      }
    }
  }
});

const MessageText = React.memo(({ text, textColor }: { text: string, textColor?: string }) => {
  return (
    <MessageTextWrapper as="sdiv">
      <MessageTextContent as="sdiv" textColor={textColor!} dangerouslySetInnerHTML={{__html: parse(text)}}/>
      <MessageTextSpacer as="sdiv"></MessageTextSpacer>
    </MessageTextWrapper>
  );
});

export const MessageFooter = React.memo(({ message, textColor, hourTextColor }: MessageFooterProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const Container = hasTextOrCaption(message) ? MessageInfoContainer : MessageInfoContainerWithoutText;
  const Info = (message.type === MessageType.Audio || hasTextOrCaption(message)) ? MessageInfo : MessageInfoWithoutText;

  const getClassByMessageType = (message: MessageModel) => {
    if (message.type === MessageType.Text) {
      return `${classes.messageWithoutMedia} ${classes.messageWithText}`;
    } else if (message.type === MessageType.Document) {
      return `${classes.messageDocument} ${classes.messageWithDocument}`;
    } else if (message.type === MessageType.Audio) {
      return '';
    } else {
      return `${classes.messageWithMedia} ${isMedia(message.type) ? (hasCaption(message) && classes.messageWithTextAndMedia) : classes.messageWithText}`;
    }
  };

  const text = message.type === MessageType.Text ? (message as TextMessage).body.body : 
    message.type === MessageType.Hsm ? (message as TemplateMessage).body.preview! : (message as MediaMessage).body.caption;

  return (
    <sdiv className={`${getClassByMessageType(message)}`}>
      {text &&
        <MessageText text={text} textColor={textColor}/>
      }
      {message.type !== MessageType.VideoConference ?
        <Container as="sdiv">
          <Info as="sdiv"
            role="button"
          >
            <MessageHour as="sdiv" dir="auto" hourColor={hourTextColor}>
              {moment(message.date).format('HH:mm')}
            </MessageHour>
          </Info>
        </Container>
      :
        <sdiv style={{ position: 'relative'}}>
          <sdiv style={{ display: 'inline' }}>
            <VideoConferenceText message={message} theme={theme}/>
          </sdiv>
        </sdiv>
      }
    </sdiv>
  );
});


export default MessageFooter;